import { useCallback, useMemo, useState } from 'react';
import type { ConfigOverrideKey } from '#dn-types/host.ts';
import { partitionConfigOverrides, type UnknownConfigOverride } from '#util/unknownConfigOverrides.ts';

/**
 * Splits saved config overrides into the ones a form can edit and the ones it can only display.
 *
 * The known bucket becomes form state as usual. The unknown bucket is held here instead - it has
 * no editor or validation schema - so it can render read-only and support deletion. Deletions are
 * tracked as indices into the saved list rather than by filtering it, so a pending delete can be
 * restored before saving and rows keep stable indices. Hand `unknownOverrides` (the saved list
 * minus pending deletes) back to `withUnknownOverrides` on save.
 *
 * @param saved The saved overrides, or undefined when creating a new resource
 * @param isEditable Narrows what this form can edit; see {@link partitionConfigOverrides}. Read
 * once at mount alongside `saved`, so it must not depend on state that changes while editing.
 */
export function useUnknownConfigOverrides<T extends UnknownConfigOverride>(
  saved: readonly T[] | undefined,
  isEditable?: (key: string) => key is ConfigOverrideKey
) {
  // Snapshot at mount, mirroring how the form captures defaultValues. removedUnknownIndices are
  // positions in this list, so a background refetch (window focus, a mutation writing the query
  // cache) must not reorder or shrink it under a pending delete - that would strike, and on save
  // silently drop, a row the user never touched.
  const [{ known: knownOverrides, unknown: savedUnknownOverrides }] = useState(() =>
    partitionConfigOverrides(saved ?? [], isEditable)
  );

  const [removedUnknownIndices, setRemovedUnknownIndices] = useState<ReadonlySet<number>>(() => new Set());

  const removeUnknownOverride = useCallback((index: number) => {
    setRemovedUnknownIndices((previous) => new Set(previous).add(index));
  }, []);

  const restoreUnknownOverride = useCallback((index: number) => {
    setRemovedUnknownIndices((previous) => {
      const next = new Set(previous);
      next.delete(index);
      return next;
    });
  }, []);

  const unknownOverrides = useMemo(
    () => savedUnknownOverrides.filter((_, index) => !removedUnknownIndices.has(index)),
    [savedUnknownOverrides, removedUnknownIndices]
  );

  const hasRemovedUnknownOverrides = removedUnknownIndices.size > 0;

  return {
    knownOverrides,
    /** The saved unknown overrides minus pending deletes; submit these */
    unknownOverrides,
    /** Every saved unknown override, including pending deletes; render these */
    savedUnknownOverrides,
    removedUnknownIndices,
    removeUnknownOverride,
    restoreUnknownOverride,
    hasRemovedUnknownOverrides,
  };
}
